import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingCart, FiSearch, FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { categories } from "../../data/products";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
];

/* ── Premium SVG Logo ── */
const LogoMark = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="logoGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#7026f7"/>
        <stop offset="100%" stopColor="#f59e0b"/>
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="10" fill="url(#logoGrad)"/>
    <path d="M8 20L14 9l4 7 3-4 5 8H8z" fill="white" opacity="0.95"/>
    <circle cx="23" cy="11" r="2" fill="white" opacity="0.7"/>
  </svg>
);

const Header = ({ onCartOpen }) => {
  const { totalItems } = useCart();
  const [scrolled, setScrolled]       = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [catOpen, setCatOpen]         = useState(false);
  const [searchOpen, setSearchOpen]   = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef(null);
  const navigate  = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled
        ? "bg-white/90 backdrop-blur-xl border-b border-gray-100/60 shadow-sm"
        : "bg-white/70 backdrop-blur-md"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <motion.div whileHover={{ rotate: 5, scale: 1.05 }} transition={{ type: "spring", stiffness: 400 }}>
              <LogoMark />
            </motion.div>
            <span className="font-display font-bold text-xl text-gray-900 tracking-tight">
              Store<span className="gradient-text">Online</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm font-display font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-brand-50 text-brand-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}

            {/* Categories dropdown */}
            <div className="relative">
              <button
                onClick={() => setCatOpen(p => !p)}
                onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-display font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
              >
                Categories
                <FiChevronDown size={13} className={`transition-transform duration-200 ${catOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {catOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 bg-white/95 backdrop-blur-xl border border-gray-100 rounded-2xl shadow-card-hover py-2 min-w-[190px] z-50 overflow-hidden"
                  >
                    {categories.slice(1).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/shop?category=${cat.id}`}
                        onClick={() => setCatOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm font-sans text-gray-700 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                      >
                        {cat.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5">
            {/* Search */}
            <AnimatePresence>
              {searchOpen && (
                <motion.form
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 220, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSearch}
                  className="hidden md:flex items-center overflow-hidden"
                >
                  <input
                    ref={searchRef}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search products…"
                    className="w-full pl-3 pr-2 py-2 text-sm border border-brand-200 rounded-xl focus:border-brand-400 focus:ring-2 focus:ring-brand-100 font-sans"
                    onBlur={() => !searchQuery && setSearchOpen(false)}
                  />
                </motion.form>
              )}
            </AnimatePresence>

            <button
              onClick={() => setSearchOpen(p => !p)}
              className="hidden md:flex btn-ghost p-2.5 text-gray-500 hover:text-brand-600"
              aria-label="Search"
            >
              {searchOpen ? <FiX size={19} /> : <FiSearch size={19} />}
            </button>

            {/* Cart */}
            <button
              onClick={onCartOpen}
              className="relative btn-ghost p-2.5 text-gray-500 hover:text-brand-600"
              aria-label={`Cart (${totalItems} items)`}
            >
              <FiShoppingCart size={19} />
              <AnimatePresence>
                {totalItems > 0 && (
                  <motion.span
                    key={totalItems}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute -top-0.5 -right-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white text-[10px] font-display font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-glow"
                  >
                    {totalItems > 99 ? "99+" : totalItems}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {/* Mobile menu */}
            <button
              onClick={() => setMobileOpen(p => !p)}
              className="md:hidden btn-ghost p-2.5 text-gray-500"
              aria-label="Menu"
            >
              {mobileOpen ? <FiX size={21} /> : <FiMenu size={21} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map(({ to, label }) => (
                <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 rounded-xl text-gray-700 hover:bg-brand-50 hover:text-brand-700 font-display font-medium transition-colors">
                  {label}
                </Link>
              ))}
              <div className="pt-3 border-t border-gray-100">
                <p className="px-4 py-1 text-xs text-gray-400 font-display font-semibold uppercase tracking-widest">Categories</p>
                {categories.slice(1).map(cat => (
                  <Link key={cat.id} to={`/shop?category=${cat.id}`} onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 rounded-xl text-gray-600 hover:bg-brand-50 hover:text-brand-700 text-sm font-sans transition-colors">
                    {cat.label}
                  </Link>
                ))}
              </div>
              <form onSubmit={handleSearch} className="pt-3 border-t border-gray-100">
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search products…"
                  className="input-field text-sm"
                />
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
